import React from 'react';
import { StackItem } from './core/StackItem';
import { StackItemGrid } from './core/StackItemGrid';
import { Capture } from './Capture';
import { useStack } from './core/StackContext';
import { StackProvider } from './core/StackContext';

// Import CardCraft and QwantaProvider for detail view
import { DNDEditor } from '../editor/DNDEditor';
import { QwantaProvider } from 'qwanyx';
import { IconButton, Select, MenuItem, FormControl } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";

// Check if we're inside a StackProvider
const useStackSafely = () => {
    try {
        return useStack();
    } catch (error) {
        return null;
    }
};

const StackContainerInner = ({
    showDetailView = false,
    detailIds = [],
    showViewSelector = true,
    DetailComponent = null,
    showCapture = true,
    showSelectionInfo = true,
    sideBySide = false,
    listWidth = "350px",
    onSelect,
    // Grid props
    viewMode = "tree",
    columnsPerRow = 3,
    imageWidth = "64px",
    ratio = "1/1",
    imageClassName = "",
    textClassName = "",
    animateClass = ""
}) => {
    // Get everything from StackContext
    const {
        selectedItems,
        lastSelectedItem,
        flattenedItems,
        cardDataMap,
        dragCompletedSignal,
        selectedItemForDetail,
        effectiveRootId,
        isCurrentCardVisible,
        setSelectedItemForDetail,
        handleSelect: contextHandleSelect,
        handleDuplicateItem,
        handleNewItem,
        refreshAfterDrop,
        registerItem,
        handleToggleCurrentCard,
        handleBackToList,
        handleDetailIdChange,
        debugLog,
        debug,
        rows
    } = useStack();

    // Wrapper for handleSelect to pass the additional parameters
    const handleSelect = (itemId, isShiftKey, isCtrlKey) => {
        contextHandleSelect(itemId, isShiftKey, isCtrlKey, showDetailView, onSelect);
    };

    // STACK OWNER: Simple vi management - Stack decides everything
    const getStackSelectedVi = () => {
        if (!selectedItemForDetail?._id || detailIds.length === 0) {
            return null;
        }
        
        // Stack is the source of truth for vi
        const stackVi = selectedItemForDetail.vi;
        const isValidVi = detailIds.some(detail => detail.id === stackVi);
        
        if (debug) {
            console.log(`[Stack Owner] getStackSelectedVi:`, {
                itemId: selectedItemForDetail._id,
                stackVi,
                isValidVi,
                availableViews: detailIds.map(d => ({ name: d.name, id: d.id }))
            });
        }
        
        // Return valid vi or first available
        return isValidVi ? stackVi : (detailIds[0]?.id || null);
    };

    const renderDetailView = () => {
        const currentVi = getStackSelectedVi();
        
        if (debug) {
            console.log(`[Stack Owner] renderDetailView:`, {
                selectedItemId: selectedItemForDetail?._id,
                currentVi,
                detailIdsCount: detailIds.length,
                selectedItemForDetail
            });
        }

        // No detailIds configured
        if (detailIds.length === 0) {
            return (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {renderDetailHeader(null)}
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                            <div style={{ fontSize: '16px', marginBottom: '8px' }}>📝 No Detail Views</div>
                            <div style={{ fontSize: '14px' }}>Configure detail views in Stack settings</div>
                        </div>
                    </div>
                </div>
            );
        }

        // No valid vi selected
        if (!currentVi) {
            return (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {renderDetailHeader(null)}
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                            <div style={{ fontSize: '16px', marginBottom: '8px' }}>👆 Select a View</div>
                            <div style={{ fontSize: '14px' }}>Choose a view from the dropdown above</div>
                        </div>
                    </div>
                </div>
            );
        }

        // STACK OWNER: Render the content - DNDEditor is just a display component
        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {renderDetailHeader(currentVi)}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    {DetailComponent ? (
                        <DetailComponent item={selectedItemForDetail} />
                    ) : (
                        // STACK OWNER: DNDEditor is isolated and controlled by Stack
                        <div 
                            key={`stack-detail-${selectedItemForDetail._id}-${currentVi}`}
                            style={{ 
                                height: '100%',
                                width: '100%',
                                overflow: 'auto',
                                border: '1px solid #eee',
                                borderRadius: '4px'
                            }}
                        >
                            <QwantaProvider _id={selectedItemForDetail._id}>
                                <DNDEditor
                                    vi={currentVi}
                                    enabled={false} // STACK RULE: Always disabled in Stack context
                                    stackMode={false} // STACK RULE: No Stack interference  
                                    readOnly={true} // STACK RULE: Always read-only in Stack
                                />
                            </QwantaProvider>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderDetailHeader = (currentVi) => {
        // STACK OWNER: Stack controls when to show dropdown
        const shouldShowDropdown = showViewSelector && detailIds.length > 1;
        
        const dropdownContent = shouldShowDropdown && (
            <FormControl size="small" style={{ minWidth: 150 }}>
                <Select
                    value={currentVi || ""}
                    onChange={handleDetailIdChange}
                    displayEmpty
                    style={{ fontSize: '14px' }}
                >
                    {!currentVi && (
                        <MenuItem value="" disabled>
                            <em>Select a view</em>
                        </MenuItem>
                    )}
                    {detailIds.map((view) => (
                        <MenuItem key={view.id} value={view.id}>
                            {view.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );

        if (sideBySide) {
            return dropdownContent && (
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px',
                    borderBottom: '1px solid #eee',
                    backgroundColor: '#f8f9fa'
                }}>
                    <span style={{ fontSize: '12px', color: '#666', marginRight: '8px' }}>View:</span>
                    {dropdownContent}
                </div>
            );
        }

        return (
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '8px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa'
            }}>
                <IconButton onClick={handleBackToList} size="small">
                    <ArrowBackIcon />
                </IconButton>
                {dropdownContent && (
                    <>
                        <span style={{ fontSize: '12px', color: '#666', margin: '0 8px' }}>View:</span>
                        {dropdownContent}
                    </>
                )}
            </div>
        );
    };

    // STACK OWNER: Stack renders its own views
    const renderStackView = () => {
        if (viewMode === "grid") {
            return (
                <StackItemGrid
                    itemId={effectiveRootId}
                    isRoot={true}
                    onSelect={handleSelect}
                    selectedItems={selectedItems}
                    registerItem={registerItem}
                    onDrop={refreshAfterDrop}
                    dragCompletedSignal={dragCompletedSignal}
                    rows={rows}
                    disableCurrentCardUpdate={showDetailView}
                    columnsPerRow={columnsPerRow}
                    imageWidth={imageWidth}
                    ratio={ratio}
                    imageClassName={imageClassName}
                    textClassName={textClassName}
                    animateClass={animateClass}
                />
            );
        }

        // Default to tree view
        return (
            <StackItem
                itemId={effectiveRootId}
                isRoot={true}
                onSelect={handleSelect}
                selectedItems={selectedItems}
                registerItem={registerItem}
                onDrop={refreshAfterDrop}
                dragCompletedSignal={dragCompletedSignal}
                rows={rows}
                disableCurrentCardUpdate={showDetailView}
            />
        );
    };

    return (
        <>
            {/* STACK OWNER: Stack controls its capture - FIXED: Pass stackRootId */}
            <div className="p-1">
                {(!selectedItemForDetail || sideBySide) && showCapture && (
                    <Capture
                        onDuplicate={handleDuplicateItem}
                        onNewItem={handleNewItem}
                        selectedItems={selectedItems}
                        stackRootId={effectiveRootId}
                    />
                )}
            </div>
            
            {/* STACK OWNER: Stack layout */}
            <div style={{
                height: showCapture ? 'calc(100% - 85px)' : '100%',
                display: 'flex',
                flexDirection: 'row'
            }}>
                {/* STACK OWNER: Stack list view */}
                <div style={{
                    width: sideBySide ? listWidth : '100%',
                    overflow: 'auto',
                    borderRight: sideBySide ? '1px solid #eee' : 'none',
                    flexShrink: 0,
                    display: sideBySide ? 'block' : (selectedItemForDetail ? 'none' : 'block')
                }}>
                    {renderStackView()}
                </div>

                {/* STACK OWNER: Stack detail view */}
                {selectedItemForDetail && (
                    <div style={{
                        flex: sideBySide ? 1 : 'none',
                        width: sideBySide ? 'auto' : '100%',
                        overflow: 'hidden',
                        borderLeft: sideBySide ? '1px solid #eee' : 'none'
                    }}>
                        {renderDetailView()}
                    </div>
                )}
            </div>
        </>
    );
};

export const StackContainer = ({
    rootId,
    debug = false,
    rows = 100,
    ...props
}) => {
    const stackContext = useStackSafely();

    if (stackContext) {
        return (
            <QwantaProvider _id={stackContext.lastSelectedItem || "guest"}>
                <StackContainerInner {...props} />
            </QwantaProvider>
        );
    }

    // If not, wrap with StackProvider for backward compatibility
    return (
        <StackProvider rootId={rootId} debug={debug} rows={rows}>
            <StackContainerInner {...props} />
        </StackProvider>
    );
};

export default StackContainer;